import { useNavigate, useParams } from 'react-router-dom';
import { FileSpreadsheet, Pencil, Trash2 } from 'lucide-react';
import { Button, ErrorState, Loading, PageHeader, Panel, StatusBadge, useConfirm, useToast } from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { hasRole } from '@/auth/types';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { GearReadTable, SightingReadTable } from '@/features/voyage-reports/components/SightingReadTables';
import { errorMessage } from '@/lib/api';
import { downloadFile } from '@/lib/download';
import { toWareki, weekdayJa } from '@/lib/wareki';
import { dms, useDeleteFixedReport, useFixedReport } from './api';
import '@/features/advance-notices/advance-notices.css';
import '@/features/voyage-reports/voyage-reports.css';

/** S-32 定時連絡 詳細 */
export function FixedReportDetailPage() {
  const { id } = useParams();
  const { data: r, isPending, error, refetch } = useFixedReport(Number(id));
  const user = useCurrentUser();
  const canEdit = hasRole(user, ['基金', '漁協']);
  const { yearPath } = useFiscalYear();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const remove = useDeleteFixedReport();
  if (isPending) return <Loading block />;
  if (error || !r) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const onPrint = async () => {
    try {
      toast.success('送信書を出力しました', await downloadFile(`/fixed-reports/${r.id}/report.xlsx`));
    } catch (e) {
      toast.error('帳票を出力できませんでした', errorMessage(e));
    }
  };
  const onDelete = async () => {
    if (!(await confirm({ title: '定時連絡を削除しますか？', message: 'この定時連絡のみで登録した視認・漁具情報も削除されます（速報と共有中の情報は残ります）。', tone: 'danger', confirmLabel: '削除する' }))) return;
    try {
      await remove.mutateAsync(r.id);
      toast.success('定時連絡を削除しました');
      navigate(yearPath('fixed-reports'));
    } catch (e) {
      toast.error('削除できませんでした', errorMessage(e));
    }
  };
  const kv: [string, string][] = [
    ['年月日', `${toWareki(r.reportDate)}（${weekdayJa(r.reportDate)}）`],
    ['時刻', r.reportTime ?? ''],
    ['緯度（N）', dms(r.latDeg, r.latMin, r.latSec)],
    ['経度（E）', dms(r.lonDeg, r.lonMin, r.lonSec)],
    ['農林漁区番号', r.zoneNo ?? ''],
    ['針路', r.course ?? ''],
    ['速力', r.speed ?? ''],
    ['天候', r.weather ?? ''],
    ['気圧', r.pressure ?? ''],
    ['視程', r.visibility ?? ''],
    ['風向', r.windDirection ?? ''],
    ['風力', r.windForce ?? ''],
    ['風浪', r.waves ?? ''],
    ['うねり', r.swell ?? ''],
  ];
  return (
    <>
      <PageHeader
        eyebrow="日々の報告 ／ 定時連絡"
        title={`定時連絡 ${r.reportDate.replaceAll('-', '/')}`}
        meta={
          <>
            {r.hasSighting && <StatusBadge tone="accent">視認あり</StatusBadge>}
            {r.hasGearFinding && <StatusBadge tone="warning">漁具あり</StatusBadge>}
          </>
        }
        description={`${r.vesselName}（${r.businessOperatorName}）　出港 ${toWareki(r.periodFrom)} ～ 入港 ${toWareki(r.periodTo)}　船長：${r.captainName ?? '—'}`}
        actions={
          <>
            <Button icon={<FileSpreadsheet size={16} />} onClick={() => void onPrint()}>
              出力
            </Button>
            {canEdit && (
              <>
                <Button variant="danger" icon={<Trash2 size={16} />} onClick={() => void onDelete()}>
                  削除
                </Button>
                <Button variant="primary" icon={<Pencil size={16} />} onClick={() => navigate(yearPath(`fixed-reports/${r.id}/edit`))} disabled={!r.noticeVesselId}>
                  編集
                </Button>
              </>
            )}
          </>
        }
      />
      <Panel title="正午の定時連絡">
        <dl className="bs-kv">
          {kv.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd className="bs-num">{v || '—'}</dd>
            </div>
          ))}
        </dl>
      </Panel>
      <Panel title="作業概要・作業予定">
        <table className="bs-work-table">
          <thead>
            <tr>
              <th>作業時間</th>
              <th>調査海域</th>
              <th>発見漁具</th>
              <th>発見船舶</th>
              <th>その他</th>
            </tr>
          </thead>
          <tbody>
            {r.workSummaries.map((w, i) => (
              <tr key={i}>
                <td className="bs-num">
                  {w.startTime ?? ''}～{w.endTime ?? ''}
                </td>
                <td>{w.surveyArea}</td>
                <td>{w.foundGear}</td>
                <td>{w.foundVessel}</td>
                <td>{w.other}</td>
              </tr>
            ))}
            {r.workPlans.map((w, i) => (
              <tr key={`p${i}`}>
                <td>
                  <StatusBadge tone="info">作業予定</StatusBadge>
                </td>
                <td>{w.surveyArea}</td>
                <td colSpan={3}>{w.other}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {r.otherNotes && <p style={{ whiteSpace: 'pre-wrap' }}>その他の記事：{r.otherNotes}</p>}
      </Panel>
      <Panel title="視認状況報告">
        <SightingReadTable rows={r.sightings ?? []} />
      </Panel>
      <Panel title="漁場調査等（外国漁船の漁具に関する情報）">
        <GearReadTable rows={r.gearFindings ?? []} />
      </Panel>
    </>
  );
}
